import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { Switch, Redirect, Route } from 'react-router-dom';
import qs from 'qs';

import {
  MODERATOR,
  BILLER,
  JORNALIST,
  EDITOR,
  CORRECTOR,
  SUPERVISOR,

  can,
} from 'utils/roles';

import Employees from 'api/resources/employees';
import { processErrorByAlert } from 'utils/getErrors';
import Alert from 'react-s-alert';
import { Deserializer } from 'jsonapi-serializer';

import ErrorBoundaryPage from 'pages/ErrorBoundaryPage';
import LoginPage from 'pages/LoginPage';
import MaterialsPage from 'pages/MaterialsPage';
import BillerPage from 'pages/BillerPage';
import EmployeesPage from 'pages/EmployeesPage';
import GalleriesPage from 'pages/GalleriesPage';
import GalleryPage from 'pages/GalleryPage';
import GalleryCreatePage from 'pages/GalleryCreatePage';
import UsersPage from 'pages/UsersPage';
import PublicationsPage from 'pages/PublicationsPage';
import SnoozesPage from 'pages/SnoozesPage';
import AuditPage from 'pages/AuditPage';
import PublicationPage from 'pages/PublicationPage';
import PublicationCreatePage from 'pages/PublicationCreatePage';
import ARPage from 'pages/ARPage';
import AvatarPage from 'pages/AvatarPage';

import Loader from 'components/Loader';
import PrivateRoute from 'components/PrivateRoute';
import Sidebar from 'components/Sidebar';
import AlertMsg from 'components/AlertMsg';

import { ProfileContext } from 'hocs/withProfile';
import AppWrapper from './AppWrapper';


const PAGES = [
  {
    path: '/materials',
    permission: 'materials',
    component: MaterialsPage,
  }, {
    path: '/biller/:department(profiles)/status/:status(created|accepted|rejected)',
    permission: 'biller',
    component: BillerPage,
  }, {
    path: '/biller/:department(requests)/status/:status(created|processing,finished|rejected|failed,failed_by_notification)',
    permission: 'biller',
    component: BillerPage,
  }, {
    path: '/users',
    permission: 'users',
    component: UsersPage,
  }, {
    path: '/employees',
    permission: 'employees',
    component: EmployeesPage,
  }, {
    path: '/galleries',
    permission: 'galleries',
    component: GalleriesPage,
    exact: true,
  }, {
    path: '/galleries/new',
    permission: 'galleries',
    component: GalleryCreatePage,
    exact: true,
  }, {
    path: '/galleries/:galleryId',
    permission: 'galleries',
    component: GalleryPage,
    exact: true,
  }, {
    path: '/snoozes',
    permission: 'snoozes',
    component: SnoozesPage,
  }, {
    path: '/publications/post_type/:post_type(all|iStory|short|longread)/state/:state(all|draft|processing|published|hidden)/sort/:sort(created_at|-created_at|published_at|-published_at)',
    permission: 'publications',
    component: PublicationsPage,
    exact: true,
  }, {
    path: '/publications/new',
    permission: 'publicationNew',
    component: PublicationCreatePage,
    exact: true,
  }, {
    path: '/publications/:publicationId',
    permission: 'publications',
    component: PublicationPage,
  }, {
    path: '/audit',
    permission: 'audit',
    component: AuditPage,
    exact: true,
  }, {
    path: '/ar',
    permission: 'ar',
    component: ARPage,
  }, {
    path: '/avatar',
    permission: 'avatar',
    component: AvatarPage,
  },
];

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      loadingLogout: false,
      profile: null,
    };

    this.can = can.bind(this);
  }

  componentDidMount() {
    this.loadProfile();
  }

  get mainPage() {
    const roles = this.state.profile ? this.state.profile.roles : [];

    if (roles.includes(SUPERVISOR)
      || roles.includes(JORNALIST)
      || roles.includes(EDITOR)
      || roles.includes(CORRECTOR)
    ) return '/publications/post_type/all/state/all/sort/-created_at';
    if (roles.includes(MODERATOR)) return '/materials';
    if (roles.includes(BILLER)) return '/biller/requests';

    return '/materials';
  }

  handleLogin = (profile) => {
    this.setState({ profile });
  }

  handleUpdateProfile = (profile) => {
    this.setState({ profile });
  }

  loadProfile = async () => {
    try {
      const profile = await Employees.getProfile({ include: 'avatar' });

      this.setState({
        profile: await new Deserializer({ keyForAttribute: 'camelCase' }).deserialize(profile),
        loading: false,
      });
    } catch (err) {
      this.setState({ loading: false });

      // 401 - это нормальное поведением, не выводим как ошибку
      if (!err.response || err.response.status !== 401) {
        processErrorByAlert(err);
      }
    }
  }

  handleLogout = async () => {
    this.setState({ loadingLogout: true });

    try {
      await Employees.signOut();

      this.setState({
        profile: null,
        loadingLogout: false,
      });
    } catch (err) {
      this.setState({ loadingLogout: false });
      processErrorByAlert(err);
    }
  }

  render() {
    return (
      <AppWrapper>
        <Helmet
          titleTemplate="%s - Baza Admin"
          defaultTitle="Baza Admin"
        >
          <meta name="description" content="A Baza Admin application" />
        </Helmet>

        {this.state.loading && <Loader />}

        {this.state.profile && (
          <ProfileContext.Provider value={{
              profile: this.state.profile,
              logout: this.handleLogout,
              loading: this.state.loadingLogout,
              update: this.handleUpdateProfile,
              can: this.can,
            }}
          >
            <Sidebar />
          </ProfileContext.Provider>
        )}

        {!this.state.loading && (
          <ProfileContext.Provider value={{ profile: this.state.profile, can: this.can }}>
            <Switch>
              {PAGES.map((page) => {
                if (this.state.profile && !this.can({
                  permission: page.permission,
                  resource: 'pages',
                })) return null;

                return (
                  <PrivateRoute
                    key={page.path}
                    path={page.path}
                    component={page.component}
                    exact={page.exact}
                    isAuth={Boolean(this.state.profile)}
                  />
                );
              })}

              <Route
                path="/login"
                render={props => (this.state.profile
                  ? (
                    <Redirect
                      to={{
                        pathname: qs.parse(window.location.search, { ignoreQueryPrefix: true }).redirect || '/',
                      }}
                    />
                  ) : (
                    <ErrorBoundaryPage>
                      <LoginPage onLoginSuccess={this.handleLogin} {...props} />
                    </ErrorBoundaryPage>
                  )
                )}
              />

              <Redirect to={this.mainPage} />
            </Switch>
          </ProfileContext.Provider>
        )}

        <Alert
          stack={{ limit: 3, spacing: 10 }}
          position="bottom-right"
          effect="bouncyflip"
          timeout={5000}
          offset={8}
          contentTemplate={AlertMsg}
        />
      </AppWrapper>
    );
  }
}

export default App;
